import * as React from 'react';

// Estado global de collapse da sidebar.
// Persistido em localStorage para sobreviver a refreshes / nova sessao.
// Chave segue convencao do app: prefixo "frotas:" + dominio.

const STORAGE_KEY = 'frotas:sidebar:collapsed';

interface SidebarContextType {
  /** Sidebar fixada/recolhida pelo usuario (true = recolhida). */
  collapsed: boolean;
  /** Alterna entre fixar aberta e fixar recolhida. */
  toggle: () => void;
  /** Define explicitamente o estado fixado. */
  setCollapsed: (next: boolean) => void;
}

const SidebarContext = React.createContext<SidebarContextType>({
  collapsed: false,
  toggle: () => {},
  setCollapsed: () => {},
});

export function useSidebar() {
  return React.useContext(SidebarContext);
}

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const [collapsed, setCollapsedState] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const setCollapsed = React.useCallback((next: boolean) => {
    setCollapsedState(next);
    try {
      localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      // localStorage indisponivel (modo privado, quota cheia): segue em memoria.
    }
  }, []);

  const toggle = React.useCallback(() => {
    setCollapsedState((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // noop
      }
      return next;
    });
  }, []);

  const value = React.useMemo(
    () => ({ collapsed, toggle, setCollapsed }),
    [collapsed, toggle, setCollapsed],
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}
